import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import {
  adminSendBtwCustomerEmail,
  adminFetchBtwEmailTemplates,
} from "../core/axios/endpoints";
import { useBodyScrollLock } from "../core/scroll-lock";
import RichTextEditor from "./rich-text-editor";

// Templates uit DB zijn plain text met \n line-breaks (legacy). Voor TipTap
// converteren we ze naar HTML met <p>-tags per paragraaf. Als de template al
// HTML-tags bevat, laten we 'm ongemoeid.
const escapeHtml = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const toEditorHtml = (text) => {
  if (!text) return "";
  if (/<\/?[a-z][^>]*>/i.test(text)) return text;
  return text
    .split(/\n\s*\n/)
    .map((p) => escapeHtml(p).replace(/\n/g, "<br />"))
    .map((p) => `<p>${p}</p>`)
    .join("");
};

const SendCustomerEmailModal = ({
  isOpen,
  applicationId,
  customerName,
  customerEmail,
  onClose,
  onSent,
}) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [toEmail, setToEmail] = useState(customerEmail ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  useBodyScrollLock(isOpen);

  // Templates ophalen bij open. We cachen niet bewust - admin kan ze net
  // bewerken hebben en we willen direct de verse versie.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setTemplatesLoading(true);
    adminFetchBtwEmailTemplates()
      .then((res) => {
        if (cancelled) return;
        setTemplates(res?.data ?? res ?? []);
      })
      .catch((e) => {
        if (cancelled) return;
        const msg =
          e?.response?.data?.error ?? e?.message ?? "Templates laden mislukt.";
        setError(msg);
        setTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSubject("");
      setBody("");
      setError(null);
    } else {
      // Bij open reset het naar-adres naar het adres uit de aanvraag, tenzij
      // admin het expliciet in deze sessie heeft aangepast.
      setToEmail(customerEmail ?? "");
    }
  }, [isOpen, customerEmail]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, submitting, onClose]);

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject ?? "");
    setBody(toEditorHtml(tpl.body ?? ""));
  };

  // TipTap output bevat HTML-tags. Voor de minimum-lengte-check willen we
  // alleen de zichtbare tekst tellen.
  const bodyTextLength = body
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim().length;

  const submit = async () => {
    if (subject.trim().length < 3 || bodyTextLength < 10) {
      setError("Onderwerp + bericht moeten gevuld zijn.");
      return;
    }
    const trimmedTo = toEmail.trim();
    if (!trimmedTo || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedTo)) {
      setError("Geef een geldig e-mailadres op om naar te sturen.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await adminSendBtwCustomerEmail(applicationId, {
        subject: subject.trim(),
        body: body.trim(),
        toEmail: trimmedTo,
      });
      onSent?.({ subject: subject.trim(), toEmail: trimmedTo });
      onClose();
    } catch (e) {
      setError(
        e?.response?.data?.error ?? e?.message ?? "Mail versturen mislukt.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm">
      <div className="my-8 w-full max-w-2xl rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-6">
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-xl text-gray-900">
              Mail naar klant
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Stuur een persoonlijk bericht aan <strong>{customerName}</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100"
            aria-label="Sluiten"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* Naar-adres (aanpasbaar) */}
          <label className="mb-5 block text-sm">
            <span className="font-medium text-gray-900">Naar</span>
            <div className="relative mt-1">
              <input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                disabled={submitting}
                placeholder="naam@voorbeeld.nl"
                className="w-full rounded-md border border-gray-300 px-3 py-2 pr-20 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
              {customerEmail && toEmail !== customerEmail ? (
                <button
                  type="button"
                  onClick={() => setToEmail(customerEmail)}
                  disabled={submitting}
                  className="absolute inset-y-0 right-2 my-auto h-7 rounded-md px-2 text-xs font-medium text-gray-600 hover:bg-gray-100"
                >
                  reset
                </button>
              ) : null}
            </div>
            {customerEmail && toEmail && toEmail !== customerEmail ? (
              <p className="mt-1 text-[11px] text-amber-700">
                Let op: dit is een ander adres dan dat van de aanvraag ({customerEmail}).
              </p>
            ) : null}
          </label>

          {/* Templates uit DB */}
          <div className="mb-5">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                Snel beginnen met
              </p>
              <Link
                to="/panel/admin/btw-email-templates"
                onClick={onClose}
                className="text-xs text-gray-500 underline hover:text-gray-900"
              >
                Templates beheren &rarr;
              </Link>
            </div>
            {templatesLoading ? (
              <p className="text-xs text-gray-400">Templates laden…</p>
            ) : templates.length === 0 ? (
              <p className="text-xs text-gray-400">
                Geen templates. Maak ze aan via &quot;Templates beheren&quot;.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {templates.map((tpl) => (
                  <button
                    key={tpl._id}
                    type="button"
                    onClick={() => applyTemplate(tpl)}
                    disabled={submitting}
                    className="rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-emerald-500 hover:bg-emerald-50 disabled:opacity-50"
                  >
                    {tpl.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <label className="mb-4 block text-sm">
            <span className="font-medium text-gray-900">Onderwerp</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Onderwerp van de mail"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              disabled={submitting}
            />
          </label>

          <div className="block text-sm">
            <span className="font-medium text-gray-900">Bericht</span>
            <div className="mt-1">
              <RichTextEditor
                value={body}
                onChange={setBody}
                disabled={submitting}
                placeholder="Schrijf hier de boodschap aan de klant. Gebruik vet, lijsten of links waar nuttig."
              />
            </div>
          </div>

          <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-900 ring-1 ring-emerald-200">
            <strong>Info:</strong> de mail wordt verstuurd vanaf{" "}
            <span className="font-mono">noreply@email.subsidie2gether.nl</span>{" "}
            met reply-to naar <span className="font-mono">info@subsidie2gether.nl</span>.
            Klant beantwoordt automatisch naar info-inbox.
          </div>

          {error ? (
            <div className="mt-3 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
              {error}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || subject.trim().length < 3 || bodyTextLength < 10}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            {submitting ? "Verzenden..." : "Verstuur mail"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SendCustomerEmailModal;
